import React from "react";
import { useInjection } from "@presentation/components/dependencyInjection";
import { UserDirectoryService } from "@application/useCases/workspace/UserDirectoryService";
import type { UserSummary } from "@domain/schemas/workspace";

const userSummaryCache = new Map<string, UserSummary>();

/**
Resolves user ids to display names for membership lists/confirmations that otherwise only have a
raw id to show. Batches every distinct set of not-yet-cached ids into a single `GET /users?ids=`
call (`UserDirectoryService.getUsersByIdsAsync`) and caches resolved entries for the session, so
navigating between pages that share members doesn't re-fetch them.
*/
export function useUserDisplayNames(userIds: readonly string[]): Record<string, UserSummary | undefined> {
  const userDirectoryService = useInjection(UserDirectoryService);
  const [, forceUpdate] = React.useState(0);
  const idsKey = React.useMemo(() => [...new Set(userIds)].toSorted((a, b) => a.localeCompare(b)).join(","), [userIds]);

  React.useEffect(() => {
    const uniqueIds = idsKey ? idsKey.split(",") : [];
    const missingIds = uniqueIds.filter((id) => !userSummaryCache.has(id));
    if (missingIds.length === 0) return;

    let isCancelled = false;
    void userDirectoryService.getUsersByIdsAsync(missingIds).then((users) => {
      if (isCancelled) return;
      for (const user of users) userSummaryCache.set(user.id, user);
      forceUpdate((version) => version + 1);
    });
    return () => {
      isCancelled = true;
    };
  }, [idsKey, userDirectoryService]);

  const result: Record<string, UserSummary | undefined> = {};
  for (const id of userIds) result[id] = userSummaryCache.get(id);
  return result;
}

/**
Falls back to the raw id while unresolved (still loading, or the id no longer exists) rather than
showing nothing.
*/
export function formatUserDisplayName(user: UserSummary | undefined, fallbackId: string): string {
  return user?.displayName ?? fallbackId;
}
